import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FeedItem, UserPreferencesRequest } from '../types/auth';

const TOKEN_KEY = 'agape_auth_token';
const ANON_USER_ID_KEY = 'agape_anon_user_id';
// CORE-1448 — идентификатор гостя для разговора с Агапеллой до регистрации.
// Его выдаёт сервер и присылает заголовком `X-Guest-Id`; храним и возвращаем
// тем же заголовком на каждом ходу.
//
// Почему не полагаемся на куку, которую сервер тоже ставит: чат-сервис живёт
// на своём домене, значит его кука для приложения СТОРОННЯЯ. Safari не
// возвращает её вовсе, Chrome и Яндекс.Браузер — в инкогнито. Тогда каждый ход
// приходит без идентификатора, сервер заводит нового гостя, и лимит не
// наступает никогда — ровно это QA и видела 03.10. Это хранилище первой
// стороны, оно переживает блокировку сторонних кук.
const GUEST_CHAT_ID_KEY = 'agape_guest_chat_id';
// CORE-1420 — Agora fallback proxy: «direct path failed here» memory + dev console switch.
const RTC_PROXY_STATE_KEY = 'agape_rtc_proxy_state';
const GUEST_PREFERENCES_KEY = 'agape_guest_preferences';
const PENDING_ACTION_KEY = 'agape_pending_action';
const COOKIE_CONSENT_KEY = 'agape_cookie_consent';
const THEME_MODE_KEY = 'agape_theme_mode';
const ONBOARDING_COMPLETED_KEY = 'agape_onboarding_completed';
// CORE-964 — «приветственное окно Фонда показано». До этого признак жил только
// в памяти провайдера, поэтому окно возвращалось при любой пересборке дерева и
// на каждой перезагрузке — а закрытая и снова всплывшая модалка неотличима от
// «кнопка не сработала», ровно так её и увидел QA.
const FUND_ONBOARDING_SEEN_KEY = 'agape_fund_onboarding_seen';
const QUICK_ROOM_HOST_HINT_KEY = 'agape_quick_room_host_hint_seen';
// CORE-809 §5 — one-time «вы можете выйти и вернуться» hint for the host of a
// session that has not started yet. Separate flag from the quick-room hint:
// different message, different moment, and dismissing one must not silence the
// other.
const LIVE_RETURN_HINT_KEY = 'agape_live_return_hint_seen';
const LANGUAGE_KEY = 'agape_language';
const TRANSLATION_LANGUAGE_KEY = 'agape_translation_language';
const VOLUME_KEY = 'agape_volume';
// CORE-1190 → CORE-1188 — громкость Live-комнаты живёт ОТДЕЛЬНО от плеера практик.
// Раньше это был один ключ на всё приложение, и он же управлял голосами участников
// (`agora.setRemoteVolume`). Достаточно было один раз выключить звук у любого видео на
// сайте — и все последующие комнаты в этом браузере открывались немыми: голоса на нуле,
// а фоновое аудио (оно намеренно выведено из-под этого ползунка, CORE-831) звучит. Ровно
// это и произошло на проде 02.09.2026. Разные сущности — разные ключи.
const ROOM_VOLUME_KEY = 'agape_room_volume';
const SAVED_EVENTS_KEY = 'agape_saved_events';
// CORE-1383 — адреса записей, у которых плеер увидел кадр 0 × 0 (только звук),
// пока бэкенд не проставил `hasVideo`. Читает карточка архива.
const AUDIO_ONLY_RECORDINGS_KEY = 'agape_audio_only_recordings';
// CORE-1920 — отметки «предупреждение о записи подтверждено» (recordingNoticeGate.ts).
const LIVE_RECORDING_ACKS_KEY = 'agape_live_recording_acks_v1';
const ABOUT_PLATFORM_WIDGET_DISMISSED_KEY = 'CORE192_about_platform_widget_dismissed';
const LIVE_DEVICE_PREFS_KEY = 'agape_live_device_prefs';
const LIVE_WIZARD_DRAFT_KEY = 'agape_live_wizard_draft';
// CORE-745 — the display name a guest typed in the Live-session green room.
// Persisted (not session-scoped) so «после обновления страницы гостевой
// сценарий остаётся работоспособным»: a reload re-spectates and the backend
// mints a fresh random handle, which would otherwise rename the guest
// mid-session.
const GUEST_DISPLAY_NAME_KEY = 'agape_guest_display_name';
// CORE-1654 — какие группы обсуждений в чате сообщества мастер свернул.
// Пока только на этом устройстве; когда появится место на бэкенде, уедет туда.
// Ключ на мастера: группы — его, и у разных сообществ они разные.
const DISCUSSION_GROUPS_COLLAPSED_KEY = 'agape_discussion_groups_collapsed';
// CORE-896 — согласие на передачу текста ИИ-чата стороннему сервису (OpenAI).
// Ключ per-user: на одном устройстве логинятся разные аккаунты, и согласие
// одного не должно молча распространяться на другого. Хранится локально
// намеренно: переустановка приложения спросит заново — для согласия это
// консервативная сторона ошибки, в отличие от «залогинен».
const AI_DATA_CONSENT_KEY_PREFIX = 'agape_ai_data_consent_v1:';
// CORE-896 — «раскрытие про автоматическую проверку публикаций показано».
// Тоже per-user и по той же причине.
const MODERATION_DISCLOSURE_KEY_PREFIX = 'agape_moderation_disclosure_v1:';
// CORE-1538 — какие окна про пробный доступ человек уже видел (оффер,
// напоминание, «закончился»). Per-user по той же причине, что и согласия выше.
const TRIAL_MOMENTS_SEEN_KEY_PREFIX = 'agape_trial_moments_seen_v1:';
// CORE-1753 — когда приложение в последний раз ушло с переднего плана и у кого.
// Нужно холодному старту: закрытая вкладка или выгруженное приложение иначе не
// знают, сколько человека не было (см. `services/appOpenedBeacon`). Один ключ,
// а не на человека: важен только последний уход, чужой отметку не тронет —
// её сверяют с вошедшим.
const APP_LEFT_MARK_KEY = 'agape_app_left_mark_v1';

/** Сохранённое решение по cookie строкой — разбирает `services/cookieChoice`. */
export type CookieConsent = string;

/**
 * CORE-1188 — что комната делает с сохранённым значением громкости.
 *
 * Ноль сюда не возвращается, и это не мелкая перестраховка. Ползунок комнаты
 * управляет голосами ЖИВЫХ участников (`agora.setRemoteVolume`), а не записью:
 * человек, вошедший в немую комнату, слышит тишину, у которой нет причины —
 * микрофоны разрешены, участники говорят, индикаторы горят. На проде 02.09.2026
 * это разбирали до тех пор, пока случайно не включили фоновую музыку, которая
 * намеренно выведена из-под этого ползунка (CORE-831) и потому звучала.
 *
 * Заглушить комнату на время по-прежнему можно — состояние живёт в открытой
 * вкладке. Не переживает оно только НОВЫЙ вход, где тишина уже необъяснима.
 * Мусор (пустая строка, `NaN`, отрицательное, больше единицы) сводится туда же,
 * куда и ноль: к слышимой комнате.
 */
export function resolveStoredRoomVolume(raw: string | null | undefined): number {
  if (raw == null) return 1;
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return Math.min(1, parsed);
}

/**
 * CORE-586 — device choices made in the Live-session green room (pre-flight),
 * persisted so the broadcast (`useRoomAgora`) acquires the same mic/camera the
 * user just verified instead of the browser default. All ids may be empty
 * strings when the user never touched the picker (→ fall back to default).
 */
export interface LiveDevicePrefs {
  micId: string;
  cameraId: string;
  speakerId: string;
  micOn: boolean;
  camOn: boolean;
}

export interface PendingAction {
  type: 'subscribe_master';
  masterId: number;
}

// ── Web session cookie (CORE-718) ──────────────────────────────────────────
//
// The JWT used to live in localStorage, which is scoped to an ORIGIN — and a
// Russian user changes origin without doing anything at all. `agapella.io`
// serves the app in place for RU addresses and 302s everyone else to
// `app.agapella.io` (CORE-306); on top of that Vercel sends RU traffic from
// `app.agapella.io` on to `app-ru.agapella.io`. So flipping a VPN moved the
// user between two storages and read as «меня разлогинило» — except the app
// never said that, it just showed empty feeds and dead chats.
//
// A cookie scoped to the shared parent domain is visible on all three hosts, so
// one login is one session. Native keeps AsyncStorage (no origins there).
//
// Security note: a JS-readable cookie is exactly as exposed to XSS as
// localStorage was — this is not a downgrade. `HttpOnly` would be strictly
// better but needs the backend to issue and accept cookie auth; this change is
// the stepping stone to that, not a substitute for it.
const TOKEN_MIGRATED_KEY = 'agape_token_cookie_migrated';
/** 30 days — comfortably longer than the JWT itself, which is the real bound. */
const TOKEN_COOKIE_MAX_AGE_S = 60 * 60 * 24 * 30;

/**
 * `; Domain=.agapella.io` only where the browser will accept it. On localhost
 * and the Vercel preview hosts a cookie claiming that domain is silently
 * dropped, which would look exactly like «login does nothing» — there we fall
 * back to a host-only cookie, and there is nothing to share anyway.
 */
function tokenCookieDomain(): string {
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  return host === 'agapella.io' || host.endsWith('.agapella.io') ? '; Domain=.agapella.io' : '';
}

function readTokenCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${TOKEN_KEY}=`;
  for (const part of document.cookie.split(';')) {
    const c = part.trim();
    if (c.startsWith(prefix)) {
      const raw = c.slice(prefix.length);
      return raw ? decodeURIComponent(raw) : null;
    }
  }
  return null;
}

function writeTokenCookie(token: string): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${TOKEN_KEY}=${encodeURIComponent(token)}; Path=/; Max-Age=${TOKEN_COOKIE_MAX_AGE_S}` +
    `; SameSite=Lax${secure}${tokenCookieDomain()}`;
}

function clearTokenCookie(): void {
  if (typeof document === 'undefined') return;
  // Expire BOTH variants: a host-only cookie left over from before the domain
  // one existed would otherwise shadow it and keep the user "logged in".
  const expired = `${TOKEN_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
  document.cookie = expired;
  const domain = tokenCookieDomain();
  if (domain) document.cookie = expired + domain;
}

/**
 * Carry an existing localStorage session into the cookie, once per origin, so
 * shipping this change doesn't sign everyone out.
 *
 * The localStorage copy is dropped as soon as it's carried over, and the
 * one-shot flag means a later sign-out can't be undone by a stale copy showing
 * up again. (A sign-out still doesn't reach a *different* origin's leftover
 * copy — but that was already true before this change, and this makes it
 * rarer, not worse.)
 */
function migrateTokenToCookie(): string | null {
  if (typeof localStorage === 'undefined') return null;
  if (localStorage.getItem(TOKEN_MIGRATED_KEY)) return null;
  const legacy = localStorage.getItem(TOKEN_KEY);
  localStorage.setItem(TOKEN_MIGRATED_KEY, '1');
  if (!legacy) return null;
  writeTokenCookie(legacy);
  localStorage.removeItem(TOKEN_KEY);
  return legacy;
}

function readWebToken(): string | null {
  return readTokenCookie() ?? migrateTokenToCookie();
}

/** CORE-1654 — разбор сохранённого списка свёрнутых групп: мусор ⇒ пусто. */
export function parseCollapsedDiscussionGroups(raw: string | null | undefined): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is number => typeof v === 'number' && Number.isInteger(v));
  } catch {
    return [];
  }
}

export const storage = {
  async getToken(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return readWebToken();
    }
    return AsyncStorage.getItem(TOKEN_KEY);
  },

  /**
   * Synchronous token read — web only. CORE-461: unload-time beacons
   * (`pagehide`) must build their request synchronously; awaiting the
   * async getToken() risks the promise never resolving before the page is
   * torn down. localStorage is synchronous, so web can read it inline.
   * Returns null off-web (native has no unload beacons).
   */
  getTokenSync(): string | null {
    if (Platform.OS === 'web') {
      // `document.cookie` is synchronous too, so the unload-beacon contract
      // this method exists for still holds after CORE-718.
      return readWebToken();
    }
    return null;
  },

  async setToken(token: string): Promise<void> {
    if (Platform.OS === 'web') {
      writeTokenCookie(token);
      // A fresh sign-in supersedes anything the migration might still pick up.
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(TOKEN_MIGRATED_KEY, '1');
        localStorage.removeItem(TOKEN_KEY);
      }
      return;
    }
    await AsyncStorage.setItem(TOKEN_KEY, token);
  },

  async removeToken(): Promise<void> {
    if (Platform.OS === 'web') {
      clearTokenCookie();
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(TOKEN_MIGRATED_KEY, '1');
        localStorage.removeItem(TOKEN_KEY);
      }
      return;
    }
    await AsyncStorage.removeItem(TOKEN_KEY);
  },

  // === Anonymous-guest identity (SCRUM-31 + 36) ===
  // BE mints a stable `anon-{uuid}` and echoes it in the X-Anon-User-Id
  // response header. We persist it so the same browser/device hits the
  // same Agora UID + Stream Chat user across page reloads + reconnects.
  async getAnonUserId(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(ANON_USER_ID_KEY);
    }
    return AsyncStorage.getItem(ANON_USER_ID_KEY);
  },

  async setAnonUserId(id: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(ANON_USER_ID_KEY, id);
      return;
    }
    await AsyncStorage.setItem(ANON_USER_ID_KEY, id);
  },

  async removeAnonUserId(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(ANON_USER_ID_KEY);
      return;
    }
    await AsyncStorage.removeItem(ANON_USER_ID_KEY);
  },

  // === Когда приложение ушло с переднего плана (CORE-1753) ===
  async getAppLeftMark(): Promise<{ userId: string; at: number } | null> {
    try {
      const raw = Platform.OS === 'web'
        ? localStorage.getItem(APP_LEFT_MARK_KEY)
        : await AsyncStorage.getItem(APP_LEFT_MARK_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed.userId === 'string' && Number.isFinite(parsed.at)) {
        return { userId: parsed.userId, at: parsed.at };
      }
      return null;
    } catch {
      // Нет отметки — рабочее состояние: перерыв посчитает сервер.
      return null;
    }
  },

  setAppLeftMark(mark: { userId: string; at: number }): void {
    const raw = JSON.stringify(mark);
    try {
      // На вебе синхронно и намеренно: пишется из `pagehide`, после которого
      // страница может не дожить до следующей микрозадачи.
      if (Platform.OS === 'web') {
        localStorage.setItem(APP_LEFT_MARK_KEY, raw);
        return;
      }
      AsyncStorage.setItem(APP_LEFT_MARK_KEY, raw).catch(() => {});
    } catch {
      /* приватный режим — останется серверная отметка */
    }
  },

  // === Идентификатор гостевого разговора с Агапеллой (CORE-1448) ===
  async getGuestChatId(): Promise<string | null> {
    try {
      if (Platform.OS === 'web') return localStorage.getItem(GUEST_CHAT_ID_KEY);
      return await AsyncStorage.getItem(GUEST_CHAT_ID_KEY);
    } catch {
      // Приватный режим может запрещать запись и чтение. Отсутствие
      // идентификатора — рабочее состояние: сервер выдаст новый.
      return null;
    }
  },

  async setGuestChatId(id: string): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.setItem(GUEST_CHAT_ID_KEY, id);
        return;
      }
      await AsyncStorage.setItem(GUEST_CHAT_ID_KEY, id);
    } catch {
      // Молча: не сохранили — следующий ход начнёт нового гостя, это хуже,
      // но ронять из-за этого разговор нельзя.
    }
  },

  async removeGuestChatId(): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        localStorage.removeItem(GUEST_CHAT_ID_KEY);
        return;
      }
      await AsyncStorage.removeItem(GUEST_CHAT_ID_KEY);
    } catch {
      // см. выше
    }
  },

  // === Agora fallback proxy (CORE-1420) ===
  async getRtcProxyState(): Promise<{ rememberedUntil?: number; forced?: boolean; relayOnly?: boolean } | null> {
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(RTC_PROXY_STATE_KEY)
      : await AsyncStorage.getItem(RTC_PROXY_STATE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  },

  async setRtcProxyState(state: { rememberedUntil?: number; forced?: boolean; relayOnly?: boolean }): Promise<void> {
    const raw = JSON.stringify(state);
    if (Platform.OS === 'web') {
      localStorage.setItem(RTC_PROXY_STATE_KEY, raw);
      return;
    }
    await AsyncStorage.setItem(RTC_PROXY_STATE_KEY, raw);
  },

  // === Saved events / library ===
  // Keyed snapshots so the «Сохранения» and «Практики» tabs survive app
  // reload before BE ships GET /api/user/me/saved. (Both are drawn with the
  // same heart since CORE-603; the split is by entity kind, not by action.)
  // The snapshot
  // is a FeedItem so we can render a card without re-fetching.
  async getSavedEvents(): Promise<FeedItem[]> {
    if (Platform.OS === 'web') {
      const data = localStorage.getItem(SAVED_EVENTS_KEY);
      return data ? JSON.parse(data) as FeedItem[] : [];
    }
    const data = await AsyncStorage.getItem(SAVED_EVENTS_KEY);
    return data ? JSON.parse(data) as FeedItem[] : [];
  },

  async setSavedEvents(items: FeedItem[]): Promise<void> {
    const serialized = JSON.stringify(items);
    if (Platform.OS === 'web') {
      localStorage.setItem(SAVED_EVENTS_KEY, serialized);
      return;
    }
    await AsyncStorage.setItem(SAVED_EVENTS_KEY, serialized);
  },

  async getAudioOnlyRecordings(): Promise<string[]> {
    try {
      const data = Platform.OS === 'web'
        ? localStorage.getItem(AUDIO_ONLY_RECORDINGS_KEY)
        : await AsyncStorage.getItem(AUDIO_ONLY_RECORDINGS_KEY);
      const parsed: unknown = data ? JSON.parse(data) : [];
      return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
    } catch {
      return [];
    }
  },

  async setAudioOnlyRecordings(keys: string[]): Promise<void> {
    const serialized = JSON.stringify(keys);
    if (Platform.OS === 'web') {
      localStorage.setItem(AUDIO_ONLY_RECORDINGS_KEY, serialized);
      return;
    }
    await AsyncStorage.setItem(AUDIO_ONLY_RECORDINGS_KEY, serialized);
  },

  /** CORE-1920 — отметки подтверждения предупреждения о записи (recordingNoticeGate.ts). */
  async getLiveRecordingAcks(): Promise<string[]> {
    try {
      const data = Platform.OS === 'web'
        ? localStorage.getItem(LIVE_RECORDING_ACKS_KEY)
        : await AsyncStorage.getItem(LIVE_RECORDING_ACKS_KEY);
      const parsed: unknown = data ? JSON.parse(data) : [];
      return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
    } catch {
      return [];
    }
  },

  async setLiveRecordingAcks(entries: string[]): Promise<void> {
    const serialized = JSON.stringify(entries);
    if (Platform.OS === 'web') {
      localStorage.setItem(LIVE_RECORDING_ACKS_KEY, serialized);
      return;
    }
    await AsyncStorage.setItem(LIVE_RECORDING_ACKS_KEY, serialized);
  },

  async clearSavedEvents(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(SAVED_EVENTS_KEY);
      return;
    }
    await AsyncStorage.removeItem(SAVED_EVENTS_KEY);
  },

  async getAboutPlatformWidgetDismissed(userId?: string | null): Promise<boolean> {
    const key = `${ABOUT_PLATFORM_WIDGET_DISMISSED_KEY}${userId ? `_${userId}` : ''}`;
    if (Platform.OS === 'web') {
      return localStorage.getItem(key) === '1';
    }
    const val = await AsyncStorage.getItem(key);
    return val === '1';
  },

  async setAboutPlatformWidgetDismissed(userId?: string | null): Promise<void> {
    const key = `${ABOUT_PLATFORM_WIDGET_DISMISSED_KEY}${userId ? `_${userId}` : ''}`;
    if (Platform.OS === 'web') {
      localStorage.setItem(key, '1');
      return;
    }
    await AsyncStorage.setItem(key, '1');
  },

  async getGuestPreferences(): Promise<UserPreferencesRequest | null> {
    if (Platform.OS === 'web') {
      const data = localStorage.getItem(GUEST_PREFERENCES_KEY);
      return data ? JSON.parse(data) : null;
    }
    const data = await AsyncStorage.getItem(GUEST_PREFERENCES_KEY);
    return data ? JSON.parse(data) : null;
  },

  async setGuestPreferences(preferences: UserPreferencesRequest): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(GUEST_PREFERENCES_KEY, JSON.stringify(preferences));
      return;
    }
    await AsyncStorage.setItem(GUEST_PREFERENCES_KEY, JSON.stringify(preferences));
  },

  async removeGuestPreferences(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(GUEST_PREFERENCES_KEY);
      return;
    }
    await AsyncStorage.removeItem(GUEST_PREFERENCES_KEY);
  },

  async getGuestDisplayName(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(GUEST_DISPLAY_NAME_KEY);
    }
    return AsyncStorage.getItem(GUEST_DISPLAY_NAME_KEY);
  },

  async setGuestDisplayName(name: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(GUEST_DISPLAY_NAME_KEY, name);
      return;
    }
    await AsyncStorage.setItem(GUEST_DISPLAY_NAME_KEY, name);
  },

  async getPendingAction(): Promise<PendingAction | null> {
    if (Platform.OS === 'web') {
      const data = localStorage.getItem(PENDING_ACTION_KEY);
      return data ? JSON.parse(data) : null;
    }
    const data = await AsyncStorage.getItem(PENDING_ACTION_KEY);
    return data ? JSON.parse(data) : null;
  },

  async setPendingAction(action: PendingAction): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
      return;
    }
    await AsyncStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
  },

  async removePendingAction(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(PENDING_ACTION_KEY);
      return;
    }
    await AsyncStorage.removeItem(PENDING_ACTION_KEY);
  },

  async getCookieConsent(): Promise<CookieConsent | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(COOKIE_CONSENT_KEY);
    }
    return AsyncStorage.getItem(COOKIE_CONSENT_KEY);
  },

  async setCookieConsent(consent: CookieConsent): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(COOKIE_CONSENT_KEY, consent);
      return;
    }
    await AsyncStorage.setItem(COOKIE_CONSENT_KEY, consent);
  },

  // === Согласие на передачу данных ИИ-сервису (CORE-896) ===
  // Пустой userId — не «нет согласия», а «спрашивать некого»: гейт всё равно
  // не пропустит, но и записать согласие в общий ключ нельзя, иначе оно
  // достанется следующему аккаунту на этом устройстве.
  async getAiDataConsent(userId: string): Promise<boolean> {
    if (!userId) return false;
    const key = AI_DATA_CONSENT_KEY_PREFIX + userId;
    if (Platform.OS === 'web') {
      return localStorage.getItem(key) === 'accepted';
    }
    return (await AsyncStorage.getItem(key)) === 'accepted';
  },

  async setAiDataConsent(userId: string, accepted: boolean): Promise<void> {
    if (!userId) return;
    const key = AI_DATA_CONSENT_KEY_PREFIX + userId;
    if (Platform.OS === 'web') {
      if (accepted) localStorage.setItem(key, 'accepted');
      else localStorage.removeItem(key);
      return;
    }
    if (accepted) await AsyncStorage.setItem(key, 'accepted');
    else await AsyncStorage.removeItem(key);
  },

  async getTrialMomentsSeen(userId: string): Promise<Record<string, boolean>> {
    if (!userId) return {};
    const key = TRIAL_MOMENTS_SEEN_KEY_PREFIX + userId;
    const raw = Platform.OS === 'web' ? localStorage.getItem(key) : await AsyncStorage.getItem(key);
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  },

  async setTrialMomentSeen(userId: string, moment: string): Promise<void> {
    if (!userId) return;
    const key = TRIAL_MOMENTS_SEEN_KEY_PREFIX + userId;
    const next = JSON.stringify({ ...(await this.getTrialMomentsSeen(userId)), [moment]: true });
    if (Platform.OS === 'web') localStorage.setItem(key, next);
    else await AsyncStorage.setItem(key, next);
  },

  async getModerationDisclosureSeen(userId: string): Promise<boolean> {
    if (!userId) return false;
    const key = MODERATION_DISCLOSURE_KEY_PREFIX + userId;
    if (Platform.OS === 'web') {
      return localStorage.getItem(key) === 'true';
    }
    return (await AsyncStorage.getItem(key)) === 'true';
  },

  async setModerationDisclosureSeen(userId: string): Promise<void> {
    if (!userId) return;
    const key = MODERATION_DISCLOSURE_KEY_PREFIX + userId;
    if (Platform.OS === 'web') {
      localStorage.setItem(key, 'true');
      return;
    }
    await AsyncStorage.setItem(key, 'true');
  },

  async getThemeMode(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(THEME_MODE_KEY);
    }
    return AsyncStorage.getItem(THEME_MODE_KEY);
  },

  async setThemeMode(mode: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(THEME_MODE_KEY, mode);
      return;
    }
    await AsyncStorage.setItem(THEME_MODE_KEY, mode);
  },

  async getOnboardingCompleted(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
    }
    const val = await AsyncStorage.getItem(ONBOARDING_COMPLETED_KEY);
    return val === 'true';
  },

  async setOnboardingCompleted(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
      return;
    }
    await AsyncStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
  },

  // CORE-964 — «приветственное окно Фонда показано».
  async getFundOnboardingSeen(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(FUND_ONBOARDING_SEEN_KEY) === 'true';
    }
    const val = await AsyncStorage.getItem(FUND_ONBOARDING_SEEN_KEY);
    return val === 'true';
  },

  async setFundOnboardingSeen(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(FUND_ONBOARDING_SEEN_KEY, 'true');
      return;
    }
    await AsyncStorage.setItem(FUND_ONBOARDING_SEEN_KEY, 'true');
  },

  // CORE quick-room — one-time "how to use a quick room" hint for hosts.
  async getQuickRoomHostHintSeen(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(QUICK_ROOM_HOST_HINT_KEY) === 'true';
    }
    const val = await AsyncStorage.getItem(QUICK_ROOM_HOST_HINT_KEY);
    return val === 'true';
  },

  async setQuickRoomHostHintSeen(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(QUICK_ROOM_HOST_HINT_KEY, 'true');
      return;
    }
    await AsyncStorage.setItem(QUICK_ROOM_HOST_HINT_KEY, 'true');
  },

  // CORE-809 §5 — one-time hint telling the host they may step out of a
  // not-yet-started session and come back through their profile.
  async getLiveReturnHintSeen(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(LIVE_RETURN_HINT_KEY) === 'true';
    }
    const val = await AsyncStorage.getItem(LIVE_RETURN_HINT_KEY);
    return val === 'true';
  },

  async setLiveReturnHintSeen(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(LIVE_RETURN_HINT_KEY, 'true');
      return;
    }
    await AsyncStorage.setItem(LIVE_RETURN_HINT_KEY, 'true');
  },

  async getLanguage(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(LANGUAGE_KEY);
    }
    return AsyncStorage.getItem(LANGUAGE_KEY);
  },

  async setLanguage(lang: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(LANGUAGE_KEY, lang);
      return;
    }
    await AsyncStorage.setItem(LANGUAGE_KEY, lang);
  },

  async getTranslationLanguage(): Promise<string | null> {
    if (Platform.OS === 'web') {
      return localStorage.getItem(TRANSLATION_LANGUAGE_KEY);
    }
    return AsyncStorage.getItem(TRANSLATION_LANGUAGE_KEY);
  },

  async setTranslationLanguage(lang: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(TRANSLATION_LANGUAGE_KEY, lang);
      return;
    }
    await AsyncStorage.setItem(TRANSLATION_LANGUAGE_KEY, lang);
  },

  getVolume(): number {
    if (Platform.OS === 'web') {
      const val = localStorage.getItem(VOLUME_KEY);
      return val != null ? parseFloat(val) : 1;
    }
    return 1;
  },

  setVolume(volume: number): void {
    if (Platform.OS === 'web') {
      localStorage.setItem(VOLUME_KEY, String(volume));
    }
  },

  /**
   * CORE-1188 — общая громкость Live-комнаты. Отдельный ключ от `getVolume()`
   * (см. `ROOM_VOLUME_KEY`), плюс страховка: сохранённый ноль в комнату не
   * возвращается.
   *
   * Ноль здесь — не настройка, а тупик. Ползунок комнаты глушит ГОЛОСА живых
   * участников, а не запись: человек, вошедший в немую комнату, слышит тишину и
   * не имеет причины связать её с ползунком — на проде 02.09 разбирались до тех
   * пор, пока случайно не включили фоновую музыку. Заглушить комнату на время
   * по-прежнему можно (в рамках открытой вкладки), но следующий вход начинается
   * со звуком.
   */
  getRoomVolume(): number {
    if (Platform.OS !== 'web') return 1;
    return resolveStoredRoomVolume(localStorage.getItem(ROOM_VOLUME_KEY));
  },

  setRoomVolume(volume: number): void {
    if (Platform.OS === 'web') {
      localStorage.setItem(ROOM_VOLUME_KEY, String(volume));
    }
  },

  // Generic JSON read/write — used by features that don't yet have a backend
  // representation (e.g. local user-curated playlists in My Library).
  async getJSON<T>(key: string): Promise<T | null> {
    try {
      if (Platform.OS === 'web') {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      }
      const raw = await AsyncStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },

  async setJSON<T>(key: string, value: T): Promise<void> {
    const serialized = JSON.stringify(value);
    if (Platform.OS === 'web') {
      localStorage.setItem(key, serialized);
      return;
    }
    await AsyncStorage.setItem(key, serialized);
  },

  async removeJSON(key: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
      return;
    }
    await AsyncStorage.removeItem(key);
  },

  // Per-live cohort-max FE fallback. Used when the BE has not yet shipped
  // SCRUM-28 (persist coHostMaxSlots on the Live entity). Master-only —
  // guests on remote devices read null and the capacity blocker becomes
  // visual-only until the BE field lands.
  async getLocalCohortMax(liveId: number): Promise<number | null> {
    const key = `agape_live_${liveId}_cohort_max`;
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(key)
      : await AsyncStorage.getItem(key);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 4) return null;
    return n;
  },

  async setLocalCohortMax(liveId: number, value: number): Promise<void> {
    const clamped = Math.max(1, Math.min(4, Math.floor(value)));
    const key = `agape_live_${liveId}_cohort_max`;
    if (Platform.OS === 'web') {
      localStorage.setItem(key, String(clamped));
      return;
    }
    await AsyncStorage.setItem(key, String(clamped));
  },

  // === Master application status (local-only, BE TBD) ===
  // After a user submits the «Стать мастером» form, the hero pill flips
  // to «На рассмотрении» and stays that way across refreshes. Keyed by
  // userId so the status is per-account. When the BE endpoint ships,
  // replace these helpers with a field on the User model — the call
  // sites in DashboardScreen / BecomeMasterModal only read/write through
  // these two functions.
  async getMasterApplicationStatus(userId: string): Promise<'pending' | null> {
    const key = `agape_master_application_${userId}`;
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(key)
      : await AsyncStorage.getItem(key);
    return raw === 'pending' ? 'pending' : null;
  },

  async setMasterApplicationStatus(userId: string, status: 'pending'): Promise<void> {
    const key = `agape_master_application_${userId}`;
    if (Platform.OS === 'web') {
      localStorage.setItem(key, status);
      return;
    }
    await AsyncStorage.setItem(key, status);
  },

  async removeMasterApplicationStatus(userId: string): Promise<void> {
    const key = `agape_master_application_${userId}`;
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
      return;
    }
    await AsyncStorage.removeItem(key);
  },

  async removeLocalCohortMax(liveId: number): Promise<void> {
    const key = `agape_live_${liveId}_cohort_max`;
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
      return;
    }
    await AsyncStorage.removeItem(key);
  },

  // Per-room paused flag — restores user's manual pause across re-entries to
  // the same practice room. Server-authoritative `PlaybackChanged` from the
  // SignalR hub still overrides this once it arrives.
  getRoomPaused(roomId: string | number): boolean {
    if (Platform.OS !== 'web') return false;
    return localStorage.getItem(`agape_room_paused_${roomId}`) === '1';
  },

  setRoomPaused(roomId: string | number, paused: boolean): void {
    if (Platform.OS !== 'web') return;
    const key = `agape_room_paused_${roomId}`;
    if (paused) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  },

  // CORE-834 — per-room flag: the host has acknowledged the «Плановое время
  // эфира истекло» modal (pressed «Продолжить эфир» or «Завершить для всех»).
  // Persisted per room so a refresh/reconnect AFTER the planned time is up does
  // not pop the mandatory modal a second time (QA «после reconnect не
  // дублируется»), while a host who reloads WITHOUT deciding still gets it — the
  // flag is written only on a button press, not on first display. Web-only,
  // like getRoomPaused: the live room is a web surface and QA covers desktop /
  // mobile web.
  getRoomPlannedExpiredAck(roomId: string | number): boolean {
    if (Platform.OS !== 'web') return false;
    return localStorage.getItem(`agape_planned_expired_ack_${roomId}`) === '1';
  },

  setRoomPlannedExpiredAck(roomId: string | number): void {
    if (Platform.OS !== 'web') return;
    localStorage.setItem(`agape_planned_expired_ack_${roomId}`, '1');
  },

  // CORE-1434 — ведущий сам выбрал состояние правой панели комнаты (закрыл её
  // или переключил вкладку); после этого панель по умолчанию не открывается.
  getRoomPanelChoice(roomId: string | number): boolean {
    if (Platform.OS !== 'web') return false;
    return localStorage.getItem(`agape_room_panel_choice_${roomId}`) === '1';
  },

  setRoomPanelChoice(roomId: string | number): void {
    if (Platform.OS !== 'web') return;
    localStorage.setItem(`agape_room_panel_choice_${roomId}`, '1');
  },

  // CORE-1832 — гость уже видел приветствие Agapella в этой комнате на этом
  // устройстве: повторный вход не раскрывает чат и не подсвечивает его заново.
  getRoomGuestWelcomeSeen(roomId: string | number): boolean {
    if (Platform.OS !== 'web') return false;
    return localStorage.getItem(`agape_room_guest_welcome_${roomId}`) === '1';
  },

  setRoomGuestWelcomeSeen(roomId: string | number): void {
    if (Platform.OS !== 'web') return;
    localStorage.setItem(`agape_room_guest_welcome_${roomId}`, '1');
  },

  // CORE-831 — per-room local multiplier the participant applies to the
  // background-audio bed (тише / выключить / вернуть). Purely local, this
  // device only: it is never sent to the room, so it must not touch the host's
  // shared background master volume or any other participant. 1 = untouched,
  // 0 = muted locally. Web-only, matching the media pipeline itself.
  getRoomBackgroundLocalVolume(roomId: string | number): number {
    if (Platform.OS !== 'web') return 1;
    const raw = localStorage.getItem(`agape_room_bg_local_vol_${roomId}`);
    if (raw == null) return 1;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return 1;
    return Math.max(0, Math.min(1, n));
  },

  setRoomBackgroundLocalVolume(roomId: string | number, value: number): void {
    if (Platform.OS !== 'web') return;
    const clamped = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 1));
    localStorage.setItem(`agape_room_bg_local_vol_${roomId}`, String(clamped));
  },

  // "I'm currently hosting an active room" flag, written by LiveRoomScreen
  // when a host mounts and cleared on unmount. Read by Quick-create so we
  // can confirm before kicking the host out of an in-flight session — see
  // docs/telemetry.md, room LwuBXx incident 2026-05-08. The TTL is a soft
  // guard for stale entries (tab closed without unmount, browser crash);
  // anything older than 4h is treated as gone.
  getActiveHostSession(): { inviteCode: string; title: string; ts: number } | null {
    if (Platform.OS !== 'web') return null;
    const raw = localStorage.getItem('agape_active_host_session');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { inviteCode: string; title: string; ts: number };
      if (!parsed.inviteCode || typeof parsed.ts !== 'number') return null;
      if (Date.now() - parsed.ts > 4 * 60 * 60 * 1000) {
        localStorage.removeItem('agape_active_host_session');
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  },

  setActiveHostSession(inviteCode: string, title: string): void {
    if (Platform.OS !== 'web') return;
    const payload = { inviteCode, title, ts: Date.now() };
    localStorage.setItem('agape_active_host_session', JSON.stringify(payload));
  },

  clearActiveHostSession(inviteCode?: string): void {
    if (Platform.OS !== 'web') return;
    // If a code is passed, only clear when it matches — avoids racing with
    // a fresh session that just wrote its own flag while we were tearing
    // the previous one down.
    if (inviteCode) {
      const current = this.getActiveHostSession();
      if (current && current.inviteCode !== inviteCode) return;
    }
    localStorage.removeItem('agape_active_host_session');
  },

  // === Live-session green-room device prefs (CORE-586) ===
  async setLiveDevicePrefs(prefs: LiveDevicePrefs): Promise<void> {
    const serialized = JSON.stringify(prefs);
    if (Platform.OS === 'web') {
      localStorage.setItem(LIVE_DEVICE_PREFS_KEY, serialized);
      return;
    }
    await AsyncStorage.setItem(LIVE_DEVICE_PREFS_KEY, serialized);
  },

  async getLiveDevicePrefs(): Promise<LiveDevicePrefs | null> {
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(LIVE_DEVICE_PREFS_KEY)
      : await AsyncStorage.getItem(LIVE_DEVICE_PREFS_KEY);
    if (!raw) return null;
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object') return p as LiveDevicePrefs;
    } catch { /* corrupt → ignore */ }
    return null;
  },

  // === Черновик создания Live-сессии ===
  // Визард — модалка на телефоне, и её состояние живёт только в памяти. Уход в
  // фон обычен, а система может выгрузить процесс в любой момент: без снимка
  // мастер терял все заполненные шаги. Пишем снимок на каждое изменение и
  // стираем при закрытии — «Выйти» намеренно начинает с чистого листа
  // (CORE-807), поэтому восстановление НЕ должно воскрешать брошенное.
  async setLiveWizardDraft(draft: unknown): Promise<void> {
    const serialized = JSON.stringify(draft);
    if (Platform.OS === 'web') {
      localStorage.setItem(LIVE_WIZARD_DRAFT_KEY, serialized);
      return;
    }
    await AsyncStorage.setItem(LIVE_WIZARD_DRAFT_KEY, serialized);
  },

  async getLiveWizardDraft<T>(): Promise<T | null> {
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(LIVE_WIZARD_DRAFT_KEY)
      : await AsyncStorage.getItem(LIVE_WIZARD_DRAFT_KEY);
    if (!raw) return null;
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object') return p as T;
    } catch { /* повреждён → как будто его нет */ }
    return null;
  },

  async clearLiveWizardDraft(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(LIVE_WIZARD_DRAFT_KEY);
      return;
    }
    await AsyncStorage.removeItem(LIVE_WIZARD_DRAFT_KEY);
  },

  // === CORE-1654 — свёрнутые группы обсуждений (локально, до бэкенда) ===
  async getCollapsedDiscussionGroups(masterProfileId: number): Promise<number[]> {
    const key = `${DISCUSSION_GROUPS_COLLAPSED_KEY}:${masterProfileId}`;
    const raw = Platform.OS === 'web'
      ? (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null)
      : await AsyncStorage.getItem(key);
    return parseCollapsedDiscussionGroups(raw);
  },

  async setCollapsedDiscussionGroups(masterProfileId: number, groupIds: readonly number[]): Promise<void> {
    const key = `${DISCUSSION_GROUPS_COLLAPSED_KEY}:${masterProfileId}`;
    const serialized = JSON.stringify([...groupIds]);
    if (Platform.OS === 'web') {
      if (typeof localStorage !== 'undefined') localStorage.setItem(key, serialized);
      return;
    }
    await AsyncStorage.setItem(key, serialized);
  },

  /** Synchronous web-only read — `useRoomAgora` needs the preferred device id
   *  at track-creation time without awaiting a promise. Null off-web. */
  getLiveDevicePrefsSync(): LiveDevicePrefs | null {
    if (Platform.OS !== 'web') return null;
    const raw = localStorage.getItem(LIVE_DEVICE_PREFS_KEY);
    if (!raw) return null;
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object') return p as LiveDevicePrefs;
    } catch { /* corrupt → ignore */ }
    return null;
  },
};
